import type { JobResponse, OpsJobItem } from '@taxassist/api-client';
import { JobStatus } from '@taxassist/api-client';
import { getStatusDisplay, type SystemStatus, type UserFriendlyStatus } from './statusMapping';
import { formatUnixTimestamp, isUnixTimestampOverdue } from './dateUtils';

// Legacy exports for backward compatibility (deprecated)
export const JOB_STATUS_LABELS: Record<JobStatus, string> = {
  DRAFT: 'Draft',
  TRIAGE: 'Triage',
  NEW_FILES: 'New Files',
  SUBMITTED: 'Submitted',
  PROCESSING: 'Processing',
  DOWNLOAD_TO_PROCESS: 'Download to Process',
  VERIFICATION: 'Verification',
  VERIFIED: 'Verified',
  DONE: 'Done',
  ERROR: 'Error',
  CANCELLED: 'Cancelled',
  ARCHIVED: 'Archived',
};

export const JOB_STATUS_COLORS: Record<JobStatus, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'default',
  TRIAGE: 'info',
  NEW_FILES: 'default',
  SUBMITTED: 'info',
  PROCESSING: 'warning',
  DOWNLOAD_TO_PROCESS: 'warning',
  VERIFICATION: 'warning',
  VERIFIED: 'success',
  DONE: 'success',
  ERROR: 'error',
  CANCELLED: 'error',
  ARCHIVED: 'default',
};

// New user-friendly status counts
type UserFriendlyStatusCount = Partial<Record<UserFriendlyStatus, number>>;

export function getStatusCounts(jobs: JobResponse[]): UserFriendlyStatusCount {
  const counts: UserFriendlyStatusCount = {};

  jobs.forEach((job) => {
    const userFriendlyStatus = getStatusDisplay(job.status as SystemStatus);
    if (userFriendlyStatus) {
      counts[userFriendlyStatus] = (counts[userFriendlyStatus] || 0) + 1;
    }
  });

  return counts;
}

export function formatJobDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return 'N/A';
  }

  const date = new Date(dateString);

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return 'N/A';
  }

  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export function getRecentJobs(jobs: JobResponse[], limit: number = 10): JobResponse[] {
  return [...jobs]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, limit);
}

/**
 * Formats a user-friendly job reference for display
 * Uses client number + filing type + tax year when available, falls back to meaningful alternatives
 */
export function formatJobReference(job: JobResponse): string {
  const taxYear = job.taxYear ? ` ${job.taxYear}` : '';

  // Extract client information from various possible locations
  const safeClientNumber = job.clientNumber && typeof job.clientNumber === 'string' ? job.clientNumber.trim() : '';

  // Check for clientName in multiple locations: direct field, metadata, or legacy name field (for backward compatibility)
  let safeClientName = '';
  if (job.clientName && typeof job.clientName === 'string') {
    safeClientName = job.clientName.trim();
  } else if (job.metadata?.clientName && typeof job.metadata.clientName === 'string') {
    safeClientName = job.metadata.clientName.trim();
  } else if ('name' in job && typeof (job as JobResponse & { name?: string }).name === 'string') {
    const legacyName = (job as JobResponse & { name?: string }).name;
    if (legacyName) {
      safeClientName = legacyName.trim();
    }
  }

  // Extract client number from metadata if not in direct field
  let finalClientNumber = safeClientNumber;
  if (!finalClientNumber && job.metadata?.clientNumber && typeof job.metadata.clientNumber === 'string') {
    finalClientNumber = job.metadata.clientNumber.trim();
  }

  // Primary: Use client number + filing type + tax year
  if (finalClientNumber && job.filingType) {
    return `${finalClientNumber} (${job.filingType}${taxYear})`;
  }

  // Fallback 1: Use client name + filing type + tax year
  if (safeClientName && job.filingType) {
    // Truncate long client names to keep display reasonable
    const truncatedName = safeClientName.length > 20 ? `${safeClientName.substring(0, 17)}...` : safeClientName;
    return `${truncatedName} (${job.filingType}${taxYear})`;
  }

  // Fallback 2: Just client number + tax year
  if (finalClientNumber) {
    return `${finalClientNumber}${taxYear}`;
  }

  // Fallback 3: Just client name + tax year (truncated)
  if (safeClientName) {
    const truncatedName = safeClientName.length > 25 ? `${safeClientName.substring(0, 22)}...` : safeClientName;
    return `${truncatedName}${taxYear}`;
  }

  // Fallback 4: Generate a meaningful reference from short jobId + filing type + tax year
  // This provides a consistent, readable format for jobs missing client data
  if (job.filingType && job.jobId) {
    const shortId = job.jobId.substring(0, 8);
    return `${shortId} (${job.filingType}${taxYear})`;
  }

  // Fallback 5: Filing type + tax year only
  if (job.filingType) {
    return `${job.filingType}${taxYear} Return`;
  }

  // Final fallback: Short job ID (first 8 characters)
  return job.jobId.substring(0, 8);
}

// Max value used to indicate "N/A" for ETAs
const MAX_ETA_NUM = Number.MAX_SAFE_INTEGER;

/**
 * Gets a formatted ETA string from a job object
 *
 * @param job - Job object that contains etaNum field
 * @returns Formatted ETA string or appropriate fallback
 */
export function getJobEtaDisplay(job: Partial<JobResponse> | OpsJobItem | Record<string, unknown>): string {
  // All jobs use etaNum as Unix timestamp
  if ('etaNum' in job && job.etaNum && typeof job.etaNum === 'number') {
    // Check if etaNum is at max value (used for jobs without real ETAs)
    if (job.etaNum === MAX_ETA_NUM) {
      return 'N/A';
    }

    try {
      // Use the centralized date formatting from dateUtils
      return formatUnixTimestamp(job.etaNum, 'MM/dd/yy hh:mm a');
    } catch {
      return 'N/A';
    }
  }

  // No ETA available
  return 'No ETA';
}

/**
 * Enhanced ETA display with business days context
 * Prefers API-provided business days information when available,
 * falls back to client-side calculation for backwards compatibility
 *
 * @param job - Job object that contains etaNum field and optional business days info
 * @param options - Display options
 * @returns Object with formatted strings for different display contexts
 */
export function getEnhancedEtaDisplay(job: Partial<JobResponse> | OpsJobItem | Record<string, unknown>, options: {
  showBusinessDays?: boolean;
  compact?: boolean;
} = {}): {
  date: string;
  businessDays: string | null;
  combined: string;
  tooltip: string;
} {
  const { showBusinessDays = true, compact = false } = options;

  // All jobs use etaNum as Unix timestamp
  if (!('etaNum' in job) || !job.etaNum || typeof job.etaNum !== 'number' || isNaN(job.etaNum)) {
    return {
      date: 'No ETA',
      businessDays: null,
      combined: 'No ETA',
      tooltip: 'No estimated completion date available'
    };
  }

  // Check if etaNum is at max value (used for jobs without real ETAs)
  if (job.etaNum === MAX_ETA_NUM) {
    return {
      date: 'N/A',
      businessDays: null,
      combined: 'N/A',
      tooltip: 'Completion date not applicable for this job status'
    };
  }

  try {
    // Format the date
    const dateStr = formatUnixTimestamp(job.etaNum, compact ? 'MMM dd' : 'MMM dd, yyyy');

    if (!showBusinessDays) {
      return {
        date: dateStr,
        businessDays: null,
        combined: dateStr,
        tooltip: `Estimated completion: ${formatUnixTimestamp(job.etaNum, 'EEEE, MMMM dd, yyyy \'at\' h:mm a')}`
      };
    }

    // Prefer API-provided business days information
    let businessDaysStr: string;
    let tooltipDetail: string;
    let isOverdue = false;

    if ('etaBusinessDaysText' in job && job.etaBusinessDaysText && typeof job.etaBusinessDaysText === 'string') {
      // Use API-provided business days text
      businessDaysStr = job.etaBusinessDaysText;
      isOverdue = 'etaIsOverdue' in job && job.etaIsOverdue === true;

      // Create compact version if needed
      if (compact) {
        // Convert "3 business days" to "3d" and "2 business days overdue" to "2d overdue"
        businessDaysStr = businessDaysStr
          .replace(/(\d+) business days overdue/, '$1d overdue')
          .replace(/(\d+) business day overdue/, '$1d overdue')
          .replace(/(\d+) business days/, '$1d')
          .replace(/(\d+) business day/, '$1d');
      }

      tooltipDetail = isOverdue ?
        'Job is past its estimated completion date (excluding weekends and holidays)' :
        'Estimated time remaining (excluding weekends and holidays)';
    } else {
      // No business days information available - this indicates a data consistency issue
      // All jobs should have etaBusinessDaysText from the API transforms
      console.warn('Job missing etaBusinessDaysText - data consistency issue', { jobId: job.jobId });
      businessDaysStr = compact ? 'N/A' : 'Business days calculation unavailable';
      tooltipDetail = 'Business days information not available - contact support if this persists';
      isOverdue = false; // Cannot determine overdue status without proper calculation
    }

    const combined = compact ?
      `${dateStr} (${businessDaysStr})` :
      `${dateStr} • ${businessDaysStr}`;

    const tooltip = `${formatUnixTimestamp(job.etaNum, 'EEEE, MMMM dd, yyyy \'at\' h:mm a')} • ${tooltipDetail}`;

    return {
      date: dateStr,
      businessDays: businessDaysStr,
      combined,
      tooltip
    };
  } catch {
    return {
      date: 'N/A',
      businessDays: null,
      combined: 'N/A',
      tooltip: 'Unable to calculate completion date'
    };
  }
}

/**
 * Checks if a job is overdue based on its ETA
 *
 * @param job - Job object that contains etaNum field
 * @returns true if the job is past its ETA
 */
export function isJobOverdue(job: Partial<JobResponse> | OpsJobItem | Record<string, unknown>): boolean {
  // All jobs use etaNum as Unix timestamp
  if ('etaNum' in job && job.etaNum && typeof job.etaNum === 'number' && job.etaNum !== MAX_ETA_NUM) {
    return isUnixTimestampOverdue(job.etaNum);
  }

  return false;
}

/**
 * Gets the number of days until ETA (negative if overdue)
 *
 * @param job - Job object that contains etaNum field
 * @returns Number of days until ETA, negative if overdue, null if no ETA
 */
export function getDaysUntilEta(job: Partial<JobResponse> | OpsJobItem | Record<string, unknown>): number | null {
  // All jobs use etaNum as Unix timestamp
  if ('etaNum' in job && job.etaNum && typeof job.etaNum === 'number' && job.etaNum !== MAX_ETA_NUM) {
    const now = new Date();
    const etaDate = new Date(job.etaNum * 1000);

    const diffTime = etaDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  return null;
}

/**
 * Sorts jobs by submittedAt timestamp
 * Jobs without submittedAt appear at the end
 *
 * @param jobs - Array of jobs to sort
 * @param order - Sort order: 'oldest' (ascending) or 'newest' (descending)
 * @returns New sorted array
 */
export function sortJobsBySubmittedAt<T extends { submittedAt?: string | null }>(
  jobs: T[],
  order: 'oldest' | 'newest' = 'oldest'
): T[] {
  return [...jobs].sort((a, b) => {
    // Jobs without submittedAt should appear at the end
    if (!a.submittedAt && !b.submittedAt) return 0;
    if (!a.submittedAt) return 1;
    if (!b.submittedAt) return -1;

    const timeA = new Date(a.submittedAt).getTime();
    const timeB = new Date(b.submittedAt).getTime();

    return order === 'newest'
      ? timeB - timeA // Newest first (descending)
      : timeA - timeB; // Oldest first (ascending)
  });
}
